import React from "react";
import { TextField as MuiTextField, Tooltip, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

/**
 * Custom TextField component with enhanced functionality
 * Supports tooltips, validation, and custom styling
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Field label text
 * @param {string} props.name - Field name for form handling
 * @param {string} props.value - Current field value
 * @param {Function} props.onChange - Change handler function
 * @param {string} props.helperText - Helper text to display in tooltip
 * @param {number} props.maxLength - Maximum character length
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.error - Error message string (from validation)
 * @param {boolean} props.touched - Whether the field has been touched
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {boolean} props.fullWidth - Whether the field should take full width
 * @param {Object} props.InputLabelProps - Additional props for InputLabel
 * @param {Object} props.inputProps - Additional props for input element
 * @param {Object} props.sx - Custom styling props
 */
const TextField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  helperText,
  maxLength,
  required = false,
  error = "",
  touched = false,
  disabled = false,
  fullWidth = true,
  InputLabelProps = {},
  inputProps = {},
  sx = {},
  ...otherProps
}) => {
  /**
   * Renders the label with optional tooltip
   * If helperText is provided, shows an info icon with tooltip
   */
  const renderLabel = () => {
    if (!label) return null;

    return (
      <>
        {label}
        {helperText && (
          <Tooltip title={helperText} arrow>
            <IconButton size="small" sx={{ padding: 0, marginLeft: "4px" }}>
              <InfoIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  /**
   * Default InputLabelProps with required field styling
   * Shows red asterisk for required fields
   */
  const defaultInputLabelProps = {
    sx: {
      "& .MuiFormLabel-asterisk": {
        color: "red",
        fontSize: "1.2rem",
      },
      ...InputLabelProps.sx,
    },
    ...InputLabelProps,
  };

  /**
   * Default inputProps with maxLength if provided
   */
  const defaultInputProps = {
    maxLength,
    ...inputProps,
  };

  /**
   * Determine if field has error and should show error message
   * Error is shown only if field has been touched and has an error message
   */
  const hasError = touched && Boolean(error);
  const errorMessage = hasError ? error : "";

  return (
    <MuiTextField
      label={renderLabel()}
      name={name}
      value={value || ""}
      onChange={onChange}
      onBlur={onBlur}
      InputLabelProps={defaultInputLabelProps}
      inputProps={defaultInputProps}
      required={required}
      error={hasError}
      helperText={errorMessage}
      fullWidth={fullWidth}
      disabled={disabled}
      sx={sx}
      {...otherProps}
    />
  );
};

export default TextField;
